import * as React from 'react';
import { Question, SidebarSimple, Lightning } from '@phosphor-icons/react';

import { Logo } from '../../components/logo';
import { AIBadge } from '../../components/ai-button';
import { Button } from '../../components/ui/button';
import { AccountDropdown, AccountDropdownProps } from './account-dropdown';

import { cn } from '@/lib/utils';
import { useBooleanFlagValue } from '@/lib/feature-flags';

interface HeaderNavProps extends React.ComponentPropsWithoutRef<'header'>, Omit<AccountDropdownProps, 'dir'> {
  isSideNavOpen?: boolean;
  onSideNavigationToggle: () => void;
  onHelpClick: () => void;
  onBrainfishNativeClick: () => void;
  onLogoNav: () => void;
  isHomePath?: boolean;
  showSuggestionAction?: boolean;
  suggestionAction?: React.ReactNode;
  onSuggestionActionClick?: () => void;
  showSidebarToggle?: boolean;
  isSuperAdmin?: boolean;
  isViewer?: boolean;
  /** Ref for the sidebar toggle button; used by layout to move focus here when sidebar is collapsed. */
  sidebarToggleRef?: React.Ref<HTMLButtonElement>;
}

export const HEADER_NAV_HEIGHT = 60;

export const HeaderNav = React.forwardRef<HTMLElement, HeaderNavProps>(function HeaderNav(
  {
    className,
    isSideNavOpen = false,
    onSideNavigationToggle,
    onHelpClick,
    onBrainfishNativeClick,
    accountName,
    accountPhoto,
    accountDropdownItems,
    teams,
    side,
    align,
    showSuggestionAction,
    suggestionAction,
    onSuggestionActionClick,
    onLogoNav,
    isHomePath = false,
    showSidebarToggle = true,
    sidebarToggleRef,
    isSuperAdmin,
    isViewer,
    ...props
  },
  ref,
) {
  const hasSuggestionAction = showSuggestionAction && !!suggestionAction && !!onSuggestionActionClick;
  const brainfishNativeEnabled = useBooleanFlagValue('platform.brainfish-native.enabled', false);
  const isHelpButtonEnabled = !isViewer;

  return (
    <header ref={ref} {...props} className={cn('px-4 py-3 relative', className)}>
      <nav aria-label="Account navigation" className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <a
            href="/"
            onClick={(e) => {
              const isPlainLeftClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

              if (isPlainLeftClick) {
                e.preventDefault();
                onLogoNav();
              }
            }}
            aria-current={isHomePath ? 'page' : undefined}
          >
            <Logo
              variant="full"
              color={isHomePath ? 'fill' : 'outline'}
              width={130}
              height={20}
              className="hidden md:block"
            />
            <Logo
              variant="mark"
              color={isHomePath ? 'fill' : 'outline'}
              width={37}
              height={20}
              className="block md:hidden"
            />
          </a>
          {showSidebarToggle && (
            <Button
              ref={sidebarToggleRef}
              size="icon"
              variant="ghost"
              aria-label="Toggle side navigation"
              className="[&_svg]:size-6"
              onClick={onSideNavigationToggle}
            >
              <SidebarSimple aria-hidden="true" weight={isSideNavOpen ? 'fill' : 'regular'} />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-4">
          {hasSuggestionAction && (
            <Button variant="suggestion" onClick={onSuggestionActionClick}>
              <Lightning weight="fill" aria-hidden="true" /> {suggestionAction}
            </Button>
          )}
          {brainfishNativeEnabled && (
            <button
              type="button"
              aria-label="Open Ask Brainfish"
              className="flex items-center gap-2 rounded border border-dark-300 bg-surface py-1 pl-1 pr-1.5 cursor-pointer hover:bg-dark-200 active:bg-dark-400 focus-visible:outline-none"
              onClick={onBrainfishNativeClick}
            >
              <AIBadge size={24} />
              <span className="text-xs font-normal text-dark-900 whitespace-nowrap">Ask Brainfish</span>
            </button>
          )}
          <AccountDropdown
            accountName={accountName}
            accountPhoto={accountPhoto}
            accountDropdownItems={accountDropdownItems}
            teams={teams}
            side={side}
            align={align}
            isSuperAdmin={isSuperAdmin}
          />
          {isHelpButtonEnabled && (
            <Button size="icon" variant="ghost" aria-label="Open help" className="[&_svg]:size-6 fixed z-10 bottom-1 left-1" onClick={onHelpClick}>
              <Question aria-hidden="true" />
            </Button>
          )}
        </div>
      </nav>
    </header>
  );
});
